import {
    ArrowForward as ArrowForwardIcon,
    Comment as CommentIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Payments as FeeIcon,
    Flag as FlagIcon,
    TrendingDown as LossIcon,
    MoneyOff as MoneyOffIcon,
    MoreVert as MoreVertIcon,
    PendingOutlined,
    TrendingUp as ProfitIcon,
    Recycling as RecyclingIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import PulsingIcon from '../../common/PulsingIcon';
import {
    Avatar,
    Box,
    Checkbox,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TableCell,
    TableRow,
    // Tooltip,
    Typography
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useFormatter } from '../../../context/FormatterContext';
import { SpamStatus, Transaction, TransactionIssueType } from '../../../services/transaction.types';
import { getDisplayCoin } from '../../../utils/displayFormatters';
import { formatCurrency } from '../../../utils/formatters';
import { canMarkAsNonTaxable, canMarkAsNotSpam, canMarkAsSpam, canMarkAsTaxable } from '../utils/transactionUtils';
import { getTranslatedType } from '../../../utils/transactionTypeTranslations';
import { useRecalculation } from '../../../context/RecalculationStatusContext';

interface TransactionTableRowProps {
    transaction: Transaction;
    isSelected: boolean;
    onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClick: () => void;
    onEditSubmit: (values: Partial<Transaction>) => void;
    onDeleteSubmit: (transaction: Transaction) => void;
    onSpamSubmit: (transaction: Transaction) => void;
    onNonTaxableSubmit: (transaction: Transaction) => void;
    onNotSpamSubmit: (transaction: Transaction) => void;
    onTaxableSubmit: (transaction: Transaction) => void;
    onActionComplete: (message: string) => void;
}

const TransactionTableRow: React.FC<TransactionTableRowProps> = ({
    transaction,
    isSelected,
    onSelect,
    onClick,
    onEditSubmit,
    onDeleteSubmit,
    onSpamSubmit,
    onNonTaxableSubmit,
    onNotSpamSubmit,
    onTaxableSubmit,
    onActionComplete,
}) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const theme = useMuiTheme();
    const { formatTransactionDate, formatCrypto } = useFormatter();

    const { status: recalculationStatus } = useRecalculation();
    const isRecalculating = !!recalculationStatus?.run || !!recalculationStatus?.new;

    const [sellImgError, setSellImgError] = useState(false);
    const [buyImgError, setBuyImgError] = useState(false);
    const [accountImgError, setAccountImgError] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    // Using any here as profitLoss is a custom property that might not be in the type definition yet
    const profitLoss = (transaction as any)?.profitLoss?.valueInCurrency;

    const isSpam = transaction.spamCoin === SpamStatus.Spam;

    // Fallback logo color
    const fallbackLogoColor = '#607d8b'; // Blue Grey

    const handleActionClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
    };

    const handleMenuAction = (action: string) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        handleActionClose();
        if (action === 'edit') {
            onEditSubmit(transaction);
        } else if (action === 'spam') {
            onSpamSubmit(transaction);
        } else if (action === 'nonTaxable') {
            onNonTaxableSubmit(transaction);
        } else if (action === 'delete') {
            onDeleteSubmit(transaction);
        } else if (action === 'notSpam') {
            onNotSpamSubmit(transaction);
        } else if (action === 'taxable') {
            onTaxableSubmit(transaction);
        }
    };

    const renderFee = () => {
        if (transaction.fee && transaction.fee !== 0 && transaction.feeCoin) {
            return (
                <ResponsiveTooltip title={t('transactions.table.tooltips.fee', { amount: formatCrypto(transaction.fee), asset: getDisplayCoin(transaction.feeTicker, transaction.feeCoin) })}>
                    <IconButton 
                        size="small"
                        sx={{ 
                            width: 32, 
                            height: 32,
                            '& .MuiSvgIcon-root': { 
                                width: 24, 
                                height: 24 
                            } 
                        }}
                    >
                        <FeeIcon color="action" />
                    </IconButton>
                </ResponsiveTooltip>
            );
        }
        return null;
    };

    const renderProfitLossIcon = () => {
        if (profitLoss !== undefined) {
            return (
                <ResponsiveTooltip title={t('transactions.table.tooltips.profitLoss', { type: profitLoss >= 0 ? t('transactions.table.profit') : t('transactions.table.loss'), amount: formatCurrency(Math.abs(profitLoss), currentCtUser?.currency || '') })}>
                    <IconButton 
                        size="small"
                        sx={{ 
                            width: 32, 
                            height: 32,
                            '& .MuiSvgIcon-root': { 
                                width: 24, 
                                height: 24 
                            } 
                        }}
                    >
                        {profitLoss >= 0 ? (
                            <ProfitIcon color="success" />
                        ) : (
                            <LossIcon color="error" />
                        )}
                    </IconButton>
                </ResponsiveTooltip>
            );
        }
        return null;
    };

    const renderTransactionDetails = () => {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                {transaction.type.in && transaction.type.out ? (
                    <>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                {formatCrypto(transaction.sell)}
                                </Box>
                            <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                                {getDisplayCoin(transaction.sellTicker, transaction.sellCoin)}
                            </Box>
                            <Avatar
                                src={transaction.sellImage ? `https://cointracking.info/img/coins/${transaction.sellImage}` : `https://cointracking.info/img/coins/${transaction.sellCoin.toLowerCase()}.png`}
                                alt={transaction.sellCoin}
                                variant="square"
                                sx={{ 
                                    width: 32,
                                    height: 32,
                                    borderRadius: sellImgError ? '50%' : 0,
                                    backgroundColor: sellImgError ? fallbackLogoColor : 'transparent',
                                    color: '#FFFFFF',
                                    fontWeight: 'bold',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& .MuiAvatar-img': {
                                        objectFit: 'contain',
                                        width: '100%',
                                        height: '100%',
                                        padding: '2px',
                                        display: sellImgError ? 'none' : 'block'
                                    }
                                }}
                                imgProps={{
                                    onError: () => setSellImgError(true)
                                }}
                            >
                                {sellImgError && (transaction.sellCoin ? transaction.sellCoin.charAt(0).toUpperCase() : '?')}
                            </Avatar>
                        </Box>
                        <ArrowForwardIcon fontSize="small" />
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                                {formatCrypto(transaction.buy)}
                            </Box>
                            <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                                {getDisplayCoin(transaction.buyTicker, transaction.buyCoin)}
                            </Box>
                            <Avatar
                                src={transaction.buyImage ? `https://cointracking.info/img/coins/${transaction.buyImage}` : `https://cointracking.info/img/coins/${transaction.buyCoin.toLowerCase()}.png`}
                                alt={transaction.buyCoin}
                                variant="square"
                                sx={{ 
                                    width: 32,
                                    height: 32,
                                    borderRadius: buyImgError ? '50%' : 0,
                                    backgroundColor: buyImgError ? fallbackLogoColor : 'transparent',
                                    color: '#FFFFFF',
                                    fontWeight: 'bold',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& .MuiAvatar-img': {
                                        objectFit: 'contain',
                                        width: '100%',
                                        height: '100%',
                                        padding: '2px',
                                        display: buyImgError ? 'none' : 'block'
                                    }
                                }}
                                imgProps={{
                                    onError: () => setBuyImgError(true)
                                }}
                            >
                                {buyImgError && (transaction.buyCoin ? transaction.buyCoin.charAt(0).toUpperCase() : '?')}
                            </Avatar>
                        </Box>
                    </>
                ) : transaction.type.in ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {formatCrypto(transaction.buy)}
                        </Box>
                        <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                            {getDisplayCoin(transaction.buyTicker, transaction.buyCoin)}
                        </Box>
                        <Avatar
                            src={transaction.buyImage ? `https://cointracking.info/img/coins/${transaction.buyImage}` : `https://cointracking.info/img/coins/${transaction.buyCoin.toLowerCase()}.png`}
                            alt={transaction.buyCoin}
                            variant="square"
                            sx={{ 
                                width: 32,
                                height: 32,
                                borderRadius: buyImgError ? '50%' : 0,
                                backgroundColor: buyImgError ? fallbackLogoColor : 'transparent',
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiAvatar-img': {
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    padding: '2px',
                                    display: buyImgError ? 'none' : 'block'
                                }
                            }}
                            imgProps={{
                                onError: () => setBuyImgError(true)
                            }}
                        >
                            {buyImgError && (transaction.buyCoin ? transaction.buyCoin.charAt(0).toUpperCase() : '?')}
                        </Avatar>
                    </Box>
                ) : (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {formatCrypto(transaction.sell)}
                        </Box>
                        <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                            {getDisplayCoin(transaction.sellTicker, transaction.sellCoin)}
                        </Box>
                        <Avatar
                            src={transaction.sellImage ? `https://cointracking.info/img/coins/${transaction.sellImage}` : `https://cointracking.info/img/coins/${transaction.sellCoin.toLowerCase()}.png`}
                            alt={transaction.sellCoin}
                            variant="square"
                            sx={{ 
                                width: 32,
                                height: 32,
                                borderRadius: sellImgError ? '50%' : 0,
                                backgroundColor: sellImgError ? fallbackLogoColor : 'transparent',
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiAvatar-img': {
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    padding: '2px',
                                    display: sellImgError ? 'none' : 'block'
                                }
                            }}
                            imgProps={{
                                onError: () => setSellImgError(true)
                            }}
                        >
                            {sellImgError && (transaction.sellCoin ? transaction.sellCoin.charAt(0).toUpperCase() : '?')}
                        </Avatar>
                    </Box>
                )}
            </Stack>
        );
    };

    const getTransactionValue = () => {
        const { type } = transaction;
        // Handle sellValueInFiat and buyValueInFiat correctly
        const sellValueInFiat = typeof transaction.sellValueInFiat === 'number' 
            ? transaction.sellValueInFiat 
            : (transaction.sellValueInFiat as any)?.value;
            
        const buyValueInFiat = typeof transaction.buyValueInFiat === 'number'
            ? transaction.buyValueInFiat
            : (transaction.buyValueInFiat as any)?.value;

        // For trades, show the outgoing (sell) value
        if (type.in && type.out && sellValueInFiat) {
            return formatCurrency(sellValueInFiat, currentCtUser?.currency || '');
        } else if (type.out && sellValueInFiat) {
            // For other transactions, show the relevant value based on direction
            return formatCurrency(sellValueInFiat, currentCtUser?.currency || '');
        } else if (type.in && buyValueInFiat) {
            return formatCurrency(buyValueInFiat, currentCtUser?.currency || '');
        }

        return '-';
    };

    const renderTransactionType = () => {
        const { type } = transaction;
        const translatedType = getTranslatedType(type);
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight="bold" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {translatedType.name}
                </Typography>
            </Stack>
        );
    };

    const renderIssueIcon = () => {
        // Early return: No icon during recalculation (top status bar shows the info)
        if (isRecalculating) {
            return null;
        }

        const issues = [];

        // Check for actual transaction issues
        if (isSpam) {
            issues.push(t('transactions.table.tooltips.spamCoin'));
        } else if (transaction.issueTypes && transaction.issueTypes.length > 0) {
            transaction.issueTypes.forEach((issueType) => {
                if (issueType !== TransactionIssueType.NoIssue) {
                    switch (issueType) {
                        case TransactionIssueType.MissingCostBasis:
                            issues.push(t('transactions.table.tooltips.missingCostBasis'));
                            break;
                        case TransactionIssueType.MissingPrice:
                            issues.push(t('transactions.table.tooltips.missingPrice'));
                            break;
                    }
                }
            });
        }

        // No issues found, no icon needed
        if (issues.length === 0) return null;

        // Create icon for transaction issues only (spam or warnings)
        const IconComponent = (
            <IconButton 
                size="small"
                sx={{ 
                    width: 32, 
                    height: 32,
                    '& .MuiSvgIcon-root': { 
                        width: 24, 
                        height: 24 
                    } 
                }}
            >
                {isSpam ? <RecyclingIcon color="warning" /> : <WarningIcon color="warning" />}
            </IconButton>
        );

        // Create multi-line tooltip
        const tooltipContent = (
            <Box>
                {issues.map((issue, index) => (
                    <Box key={index} component="div">
                        {issue}
                    </Box>
                ))}
            </Box>
        );

        // Always show tooltip for transaction issues
        return (
            <ResponsiveTooltip title={tooltipContent}>
                {IconComponent}
            </ResponsiveTooltip>
        );
    };

    const renderActionIcons = () => {
        const commonIcons = (
            <IconButton 
                size="small" 
                onClick={handleActionClick}
                sx={{ 
                    width: 32, 
                    height: 32,
                    '& .MuiSvgIcon-root': { 
                        width: 24, 
                        height: 24 
                    } 
                }}
            >
                <MoreVertIcon />
            </IconButton>
        );

        return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {renderFee()}
                {renderProfitLossIcon()}
                {renderIssueIcon()}
                {transaction.comment && (
                    <ResponsiveTooltip title={transaction.comment}>
                        <IconButton 
                            size="small"
                            sx={{ 
                                width: 32, 
                                height: 32,
                                '& .MuiSvgIcon-root': { 
                                    width: 24, 
                                    height: 24 
                                } 
                            }}
                        >
                            <CommentIcon />
                        </IconButton>
                    </ResponsiveTooltip>
                )}
                {commonIcons}
            </Stack>
        );
    };

    return (
        <>
            <TableRow
                hover
                onClick={onClick}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={-1}
                selected={isSelected}
                sx={{
                    cursor: 'pointer',
                    '&:last-child td, &:last-child th': {
                        border: 0,
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    },
                    opacity: isSpam ? 0.6 : 1,
                }}
            >
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        checked={isSelected}
                        onChange={onSelect}
                        onClick={(e) => e.stopPropagation()}
                    />
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                            {formatTransactionDate(transaction.date).date}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatTransactionDate(transaction.date).time}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        overflow: 'hidden'
                    }}>
                        <Avatar
                            src={transaction.platformImage ? `https://cointracking.info/${transaction.platformImage}` : (transaction.account ? `https://cointracking.info/img/exchanges/${transaction.account.toLowerCase().replace(/\s+/g, '')}.png` : '')}
                            alt={transaction.account || '-'}
                            variant="square"
                            sx={{ 
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                                borderRadius: accountImgError ? '50%' : 0,
                                backgroundColor: accountImgError ? fallbackLogoColor : 'transparent',
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiAvatar-img': {
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    padding: '2px',
                                    display: accountImgError ? 'none' : 'block'
                                }
                            }}
                            imgProps={{
                                onError: () => setAccountImgError(true)
                            }}
                        >
                            {accountImgError && (transaction.account ? transaction.account.charAt(0).toUpperCase() : '?')}
                        </Avatar>
                        <Typography variant="body2" fontWeight="bold" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {transaction.account || '-'}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    {renderTransactionType()}
                </TableCell>
                <TableCell>
                    <Box sx={{ 
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        {renderTransactionDetails()}
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <Box component="div" sx={{ typography: 'body2', fontWeight: 600 }}>
                        {getTransactionValue()}
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>{renderActionIcons()}</TableCell>
            </TableRow>

            <Menu
                anchorEl={anchorEl}
                id="transaction-menu"
                open={open}
                onClose={handleActionClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleMenuAction('edit')}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.edit')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuAction('delete')}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.delete')}</ListItemText>
                </MenuItem>
                {canMarkAsSpam(transaction) && !isSpam && (
                    <MenuItem onClick={handleMenuAction('spam')}>
                        <ListItemIcon>
                            <FlagIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.markAsSpam')}</ListItemText>
                    </MenuItem>
                )}
                {canMarkAsNotSpam(transaction) && isSpam && (
                    <MenuItem onClick={handleMenuAction('notSpam')}>
                        <ListItemIcon>
                            <FlagIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.removeFromSpam')}</ListItemText>
                    </MenuItem>
                )}
                {canMarkAsNonTaxable(transaction) && (
                    <MenuItem onClick={handleMenuAction('nonTaxable')}>
                        <ListItemIcon>
                            <MoneyOffIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.nonTaxable')}</ListItemText>
                    </MenuItem>
                )}
                {canMarkAsTaxable(transaction) && (
                    <MenuItem onClick={handleMenuAction('taxable')}>
                        <ListItemIcon>
                            <MoneyOffIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>{t('transactions.table.actions.taxable')}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default TransactionTableRow;
